<template>
  <div class="ws-root">

    <div class="ws-header">
      <div>
        <h1 class="ws-title">Worksheets</h1>
      </div>
      <div class="ws-count-badge">{{ packs.length }} workbooks</div>
    </div>

    <!-- Tabs -->
    <div class="ws-tabs">
      <button
        class="ws-tab"
        :class="{ 'ws-tab-active': activeTab === 'topic' }"
        @click="activeTab = 'topic'"
      >Topic Worksheets</button>
      <button
        class="ws-tab"
        :class="{ 'ws-tab-active': activeTab === 'practice' }"
        @click="activeTab = 'practice'"
      >Practice Worksheets</button>
    </div>

    <!-- Tab description -->
    <p v-if="activeTab === 'practice'" class="ws-tab-desc">
      Fresh practice sets generated every day — new questions each morning covering all topic areas. Download, print and build exam confidence.
    </p>
    <p v-else class="ws-tab-desc">
      Focused worksheets for every topic area in the 11+ maths curriculum. Pick a subject, download today's sheet, and practice as needed.
    </p>

    <!-- Upgrade banner -->
    <div v-if="showUpgradeBanner" class="ws-upgrade-banner">
      <MTIcon name="lock" :size="18" class="ws-upgrade-icon" />
      <span>
        Most worksheets require <strong>Pro Plus</strong> or <strong>Family</strong>.
        <RouterLink :to="isAuthenticated ? '/parent/subscription' : '/auth/register'" class="ws-upgrade-link">Upgrade your plan →</RouterLink>
      </span>
    </div>

    <!-- Loading / error states -->
    <div v-if="packsLoading" class="ws-loading">Loading worksheets…</div>
    <div v-else-if="packsError" class="ws-error">{{ packsError }}</div>

    <!-- ── BOOKSHELF ── -->
    <div v-else class="ws-bookcase">

      <!-- Top cap -->
      <div class="ws-bc-cap"></div>

      <!-- Cabinet body -->
      <div class="ws-bc-body">
        <!-- Left wall -->
        <div class="ws-bc-wall ws-bc-wall-l"></div>

        <!-- Shelves -->
        <div class="ws-bc-shelves">
          <template v-for="(row, ri) in shelfRows" :key="ri">
            <!-- Shelf bay -->
            <div class="ws-bc-bay">
              <!-- Top-of-bay shadow (gives depth to the cabinet back) -->
              <div class="ws-bc-bay-shadow"></div>
              <!-- Books -->
              <div class="ws-bc-row">
                <div
                  v-for="pack in row"
                  :key="pack._id"
                  class="ws-bc-book"
                  :class="{
                    'ws-bc-book-locked': pack.locked,
                    'ws-bc-book-active': selectedPack?._id === pack._id
                  }"
                  :style="{ '--book-color': pack.color.bg, '--book-light': pack.color.light }"
                  @click="toggleBook(pack)"
                >
                  <!-- Lock overlay -->
                  <div v-if="pack.locked" class="ws-lock-overlay ws-lock-overlay-sm">
                    <div class="ws-lock-inner">
                      <MTIcon name="lock" :size="18" />
                      <span>{{ requiredPlanLabel(pack.excludedPlans) }}</span>
                    </div>
                  </div>
                  <!-- Cover -->
                  <div class="ws-cover">
                    <div class="ws-cover-header">
                      <div class="ws-cover-brand-group">
                        <img src="/new-logo.png" class="ws-cover-logo-mark" alt="">
                        <span class="ws-cover-brand">Maths Eleven Plus</span>
                      </div>
                      <span class="ws-cover-icon">{{ pack.color.icon }}</span>
                    </div>
                    <div class="ws-cover-body">
                      <div class="ws-cover-subject">11+ Maths</div>
                      <h2 class="ws-cover-title">{{ pack.title }}</h2>
                      <p class="ws-cover-desc">{{ pack.desc }}</p>
                    </div>
                    <div class="ws-cover-footer">
                      <div class="ws-cover-meta">
                        <span class="ws-cover-size">{{ pack.size }} questions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shelf plank -->
            <div class="ws-bc-plank">
              <div class="ws-bc-plank-edge"></div>
            </div>
          </template>
        </div>

        <!-- Right wall -->
        <div class="ws-bc-wall ws-bc-wall-r"></div>
      </div>

    </div>

  </div>

  <!-- ── Book detail popup ── -->
  <Teleport to="body">
    <transition name="ws-modal">
      <div v-if="selectedPack" class="ws-modal-overlay" @click.self="closeModal">
        <div
          class="ws-modal"
          :style="{ '--book-color': selectedPack.color.bg, '--book-light': selectedPack.color.light }"
        >
          <!-- Close -->
          <button class="ws-modal-close" @click="closeModal">✕</button>

          <!-- Mini cover preview -->
          <div class="ws-modal-preview">
            <div class="ws-modal-book">
              <div class="ws-cover">
                <div class="ws-cover-header">
                  <span class="ws-cover-brand">Maths Eleven Plus</span>
                  <span class="ws-cover-icon">{{ selectedPack.color.icon }}</span>
                </div>
                <div class="ws-cover-body">
                  <div class="ws-cover-subject">11+ Maths</div>
                  <h2 class="ws-cover-title">{{ selectedPack.title }}</h2>
                  <p class="ws-cover-desc">{{ selectedPack.desc }}</p>
                </div>
                <div class="ws-cover-footer">
                  <div class="ws-cover-meta">
                    <span class="ws-cover-size">{{ selectedPack.size }} questions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Details -->
          <div class="ws-modal-body">
            <p class="ws-modal-subject">11+ Maths Worksheet</p>
            <h2 class="ws-modal-title">{{ selectedPack.title }}</h2>
            <p class="ws-modal-desc">{{ selectedPack.desc }}</p>

            <div class="ws-modal-pills">
              <span class="ws-modal-pill">📝 {{ selectedPack.size }} questions</span>
              <span class="ws-modal-pill">🎓 11+ Maths</span>
            </div>

            <p class="ws-modal-note">
              A fresh worksheet is generated every day — new questions each morning, ready to download and print.
            </p>

            <!-- Loading -->
            <button v-if="loadingPdf" class="ws-modal-download ws-modal-download-loading" disabled>
              <MTIcon name="loader" :size="16" class="ws-spin" />
              Checking availability…
            </button>

            <!-- Error -->
            <div v-else-if="worksheetError" class="ws-modal-unavailable">
              <MTIcon name="alert-circle" :size="16" />
              {{ worksheetError }}
            </div>

            <!-- PDF ready — direct download link -->
            <a
              v-else-if="worksheetData?.pdfReady"
              :href="worksheetData.pdfUrl"
              target="_blank"
              rel="noopener"
              class="ws-modal-download"
            >
              <MTIcon name="download" :size="16" />
              Download PDF
              <span class="ws-modal-date">{{ worksheetData.date }}</span>
            </a>

            <!-- PDF not ready yet -->
            <div v-else-if="worksheetData" class="ws-modal-unavailable">
              <MTIcon name="clock" :size="16" />
              Today's worksheet is being prepared — check back shortly.
            </div>

            <!-- Unauthenticated: prompt sign-in -->
            <div v-else-if="!isAuthenticated" class="ws-modal-signin">
              <p class="ws-modal-signin-text">Sign in to download today's worksheet.</p>
              <div class="ws-modal-signin-actions">
                <RouterLink to="/auth/login" class="ws-modal-download">Sign in</RouterLink>
                <RouterLink to="/auth/register" class="ws-modal-download ws-modal-download-outline">Begin Free</RouterLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { useAuth } from '@/composables/useAuth';
import { usePageMeta } from '@/composables/usePageMeta';
import { isLessonLocked, requiredPlanLabel } from '@/composables/usePlanGating';
import { packCover } from '@/services/worksheetCovers';
import { worksheetApi } from '@/services/worksheetApi';
import MTIcon from '@/components/ui/MTIcon.vue';

const userStore = useUserStore();
const { isAuthenticated } = useAuth();
const userPlan  = computed(() => userStore.effectivePlan);

const activeTab = ref('topic');

const packsRaw     = ref([]);
const packsLoading = ref(true);
const packsError   = ref(null);

const selectedPack   = ref(null);
const worksheetData  = ref(null);   // API response for selected pack
const worksheetError = ref(null);   // error message if fetch failed
const loadingPdf     = ref(false);  // true while fetching worksheet data

async function loadPacks() {
  packsLoading.value = true;
  packsError.value   = null;
  packsRaw.value     = [];
  closeModal();
  try {
    const type = activeTab.value === 'practice' ? 'PRACTICE' : 'TOPIC';
    const { packs } = await worksheetApi.getPacks(type);
    packsRaw.value = packs ?? [];
  } catch (err) {
    packsError.value = 'Could not load worksheets. Please try again.';
  } finally {
    packsLoading.value = false;
  }
}

onMounted(() => {
  usePageMeta({
    title:       'Free 11+ Maths Worksheets — Download & Print | Maths Eleven Plus',
    description: 'Download free daily 11+ maths worksheets covering all 47 topic areas. Fresh questions generated every day — print-ready PDFs for Grammar School preparation.',
    canonical:   'https://mathselevenplus.co.uk/worksheets',
  });
  loadPacks();
});

watch(activeTab, loadPacks);

// ── JSON-LD structured data ───────────────────────────────────────────────────
function injectSchema(packList) {
  removeSchema();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Free 11+ Maths Worksheets',
    description: 'Daily generated 11+ maths worksheets covering all topic areas for Grammar School preparation.',
    url: 'https://mathselevenplus.co.uk/worksheets',
    numberOfItems: packList.length,
    itemListElement: packList.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.title,
      description: p.desc,
    })),
  };
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id   = 'ws-schema';
  el.text = JSON.stringify(schema);
  document.head.appendChild(el);
}

function removeSchema() {
  document.getElementById('ws-schema')?.remove();
}

watch(packsRaw, (list) => { if (list.length) injectSchema(list); });
onUnmounted(removeSchema);

const BOOKS_PER_SHELF = 4;

async function toggleBook(pack) {
  if (pack.locked) return;

  // Clicking the same book again closes the modal
  if (selectedPack.value?._id === pack._id) {
    selectedPack.value   = null;
    worksheetData.value  = null;
    worksheetError.value = null;
    return;
  }

  selectedPack.value   = pack;
  worksheetData.value  = null;
  worksheetError.value = null;

  // Unauthenticated users see the sign-in prompt — no API call needed
  if (!isAuthenticated.value) return;

  loadingPdf.value = true;
  try {
    worksheetData.value = await worksheetApi.getDailyWorksheet(pack._id);
  } catch (err) {
    worksheetError.value = err.status === 404
      ? 'No worksheet available yet — check back tomorrow.'
      : 'Could not load worksheet. Please try again.';
  } finally {
    loadingPdf.value = false;
  }
}

function closeModal() {
  selectedPack.value   = null;
  worksheetData.value  = null;
  worksheetError.value = null;
}

const packs = computed(() =>
  packsRaw.value.map((p, index) => ({
    ...p,
    color:  packCover(p.colorScheme ?? index),
    locked: isAuthenticated.value && isLessonLocked(p.excludedPlans, userPlan.value),
  }))
);

const shelfRows = computed(() => {
  const rows = [];
  for (let i = 0; i < packs.value.length; i += BOOKS_PER_SHELF) {
    rows.push(packs.value.slice(i, i + BOOKS_PER_SHELF));
  }
  return rows;
});

const showUpgradeBanner = computed(() => isAuthenticated.value && packs.value.some(p => p.locked));
</script>

<style>
.ws-root {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px 64px;
}

/* ── Header ── */
.ws-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
}
.ws-title { font-size: 24px; font-weight: 700; color: var(--mt-text); margin-bottom: 4px; }
.ws-tab-desc {
  font-size: 14px;
  color: var(--mt-text-muted, #64748b);
  line-height: 1.6;
  margin-bottom: 24px;
  max-width: 640px;
}
.ws-count-badge {
  background: var(--mt-accent-blue-muted); color: var(--mt-accent-blue);
  font-size: 12px; font-weight: 600; padding: 4px 14px;
  border-radius: 20px; white-space: nowrap; align-self: center;
}

/* ── Loading / error ── */
.ws-loading { color: #94a3b8; font-size: 14px; padding: 40px 0; text-align: center; }
.ws-error   { color: #ef4444; font-size: 14px; padding: 40px 0; text-align: center; }

/* ── Tabs ── */
.ws-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;
}
.ws-tab {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: var(--mt-text-muted, #94a3b8);
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.ws-tab:hover { color: var(--mt-text, #1e293b); }
.ws-tab-active {
  color: var(--mt-text, #1e293b);
  font-weight: 700;
  border-bottom-color: #B8902A;
}

/* ── Upgrade banner ── */
.ws-upgrade-banner {
  display: flex; align-items: center; gap: 10px;
  background: #fefce8; border: 1px solid #fde68a;
  border-radius: 10px; padding: 12px 18px;
  font-size: 14px; color: #78350f; margin-bottom: 28px;
}
.ws-upgrade-icon { color: #d97706; flex-shrink: 0; }
.ws-upgrade-link { color: #b45309; font-weight: 600; text-decoration: none; margin-left: 4px; }
.ws-upgrade-link:hover { text-decoration: underline; }

/* ── Shared cover ── */
.ws-cover {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  border-radius: 2px 6px 6px 2px;
  overflow: hidden; background: white;
  position: relative; z-index: 1;
}
.ws-cover-header {
  background: var(--book-color);
  padding: 14px 14px 12px;
  display: flex; justify-content: space-between; align-items: flex-start;
  flex-shrink: 0;
}
.ws-cover-brand-group {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ws-cover-logo-mark {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  flex-shrink: 0;
}
.ws-cover-brand {
  font-size: 9px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: rgba(255,255,255,0.75); line-height: 1.3; max-width: 80px;
}
.ws-cover-icon {
  width: 34px; height: 34px; background: white; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.15);
}
.ws-cover-body {
  flex: 1; padding: 12px 14px 8px;
  background: var(--book-light); display: flex; flex-direction: column;
}
.ws-cover-subject {
  font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--book-color); margin-bottom: 6px; opacity: 0.75;
}
.ws-cover-title {
  font-size: 14px; font-weight: 800; color: var(--book-color);
  line-height: 1.25; margin-bottom: 6px; letter-spacing: -0.01em;
}
.ws-cover-desc { font-size: 11px; color: #555; line-height: 1.5; flex: 1; }
.ws-cover-footer {
  padding: 8px 14px; background: white;
  border-top: 1px solid var(--book-light);
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  flex-shrink: 0;
}
.ws-cover-meta { display: flex; flex-direction: column; gap: 1px; }
.ws-cover-size { font-size: 11px; font-weight: 700; color: var(--book-color); }

/* Shared buttons */
.ws-download-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 12px; background: var(--book-color);
  color: white; border: none; border-radius: 6px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: opacity 0.15s; white-space: nowrap;
}
.ws-download-btn:hover { opacity: 0.85; }
/* Shared lock overlay */
.ws-lock-overlay {
  position: absolute; inset: 0; z-index: 10;
  display: flex; align-items: center; justify-content: center;
  border-radius: inherit;
  background: rgba(255,255,255,0.52);
  backdrop-filter: blur(2px);
}
.ws-lock-inner {
  display: flex; flex-direction: column; align-items: center; gap: 5px;
  background: rgba(255,255,255,0.93); border: 1px solid #e2e8f0;
  border-radius: 10px; padding: 10px 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1); color: #374151;
}
.ws-lock-inner span {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #6b7280; white-space: nowrap;
}

/* ── BOOKSHELF VIEW ── */
.ws-bookcase {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 16px 56px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.16);
}

/* Top cap — thick top rail */
.ws-bc-cap {
  height: 28px;
  background: linear-gradient(180deg, #6b3a1f 0%, #4a2510 60%, #3a1c0a 100%);
  border-bottom: 2px solid #1a0a03;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.4);
}

/* Cabinet body */
.ws-bc-body {
  display: flex;
  background: #110800; /* dark cabinet back */
}

/* Side walls */
.ws-bc-wall {
  width: 32px;
  flex-shrink: 0;
  background: linear-gradient(to right, #5a3018, #7a4a28, #5a3018);
}
.ws-bc-wall-l {
  box-shadow: inset -6px 0 14px rgba(0,0,0,0.5), inset 2px 0 4px rgba(255,255,255,0.06);
}
.ws-bc-wall-r {
  background: linear-gradient(to right, #4a2810, #6a3a20, #4a2810);
  box-shadow: inset 6px 0 14px rgba(0,0,0,0.5), inset -2px 0 4px rgba(255,255,255,0.04);
}

/* Shelves column */
.ws-bc-shelves { flex: 1; }

/* Each shelf bay (books + space above plank) */
.ws-bc-bay {
  position: relative;
  padding: 20px 20px 12px;
  background:
    linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 28%),
    #1a0d05;
}
/* Top-of-bay depth shadow — the "ceiling" of the shelf */
.ws-bc-bay-shadow {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 20px;
  background: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%);
  pointer-events: none;
}

.ws-bc-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  align-items: flex-end; /* books rest on the plank */
}

/* Individual book on shelf */
.ws-bc-book {
  position: relative;
  aspect-ratio: 3 / 4;
  /* Subtle perspective — top leans slightly back, as if standing on a shelf */
  transform: perspective(800px) rotateX(4deg) translateY(2px);
  transform-origin: bottom center;
  transform-style: preserve-3d;
  transition: transform 0.28s ease, box-shadow 0.28s ease;
  border-radius: 2px 5px 5px 2px;
  box-shadow:
    0 8px 20px rgba(0,0,0,0.45),
    0 2px 6px rgba(0,0,0,0.3),
    4px 0 8px rgba(0,0,0,0.2);
  cursor: pointer;
}
.ws-bc-book:not(.ws-bc-book-locked):hover {
  transform: perspective(800px) rotateX(0deg) translateY(-12px) scale(1.03);
  box-shadow:
    0 24px 40px rgba(0,0,0,0.5),
    0 8px 16px rgba(0,0,0,0.3),
    4px 0 8px rgba(0,0,0,0.15);
  z-index: 2;
}
.ws-bc-book-active {
  transform: perspective(800px) rotateX(0deg) translateY(-8px) scale(1.02) !important;
  box-shadow:
    0 20px 36px rgba(0,0,0,0.5),
    0 0 0 2px rgba(255,255,255,0.4),
    4px 0 8px rgba(0,0,0,0.2) !important;
  z-index: 2;
}
.ws-bc-book-locked { opacity: 0.5; cursor: default; }

/* Spine gradient on book's left edge */
.ws-bc-book::before {
  content: ''; position: absolute; inset: 0; border-radius: inherit;
  background: linear-gradient(to right, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.1) 15%, transparent 28%);
  pointer-events: none; z-index: 5;
}
/* Page edges */
.ws-bc-book::after {
  content: ''; position: absolute;
  top: 2px; right: -7px; bottom: 2px; width: 7px;
  border-radius: 0 2px 2px 0;
  background: repeating-linear-gradient(to bottom, #f8f6f3 0px, #f8f6f3 2px, #dedad6 2px, #dedad6 3px);
  box-shadow: 2px 1px 6px rgba(0,0,0,0.2);
  z-index: -1;
}

/* Shelf plank */
.ws-bc-plank {
  height: 22px;
  background: linear-gradient(180deg, #d4a05a 0%, #b07838 55%, #8a5820 100%);
  box-shadow:
    0 8px 20px rgba(0,0,0,0.45),
    inset 0 2px 0 rgba(255,255,255,0.22),
    inset 0 -1px 0 rgba(0,0,0,0.35);
  position: relative;
  overflow: hidden;
}
/* Wood grain on plank */
.ws-bc-plank-edge {
  position: absolute; inset: 0;
  background: repeating-linear-gradient(
    90deg,
    transparent 0px, transparent 48px,
    rgba(0,0,0,0.05) 48px, rgba(0,0,0,0.05) 50px
  );
}

/* ── Modal popup ── */
.ws-modal-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.ws-modal {
  position: relative;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  max-width: 560px;
  display: flex;
  box-shadow: 0 32px 80px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2);
}
.ws-modal-close {
  position: absolute; top: 14px; right: 14px;
  width: 30px; height: 30px; border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.85); font-size: 13px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s; z-index: 2;
}
.ws-modal-close:hover { background: rgba(255,255,255,0.28); color: white; }

/* Left: mini book cover */
.ws-modal-preview {
  width: 180px;
  flex-shrink: 0;
  background: var(--book-color);
  display: flex; align-items: center; justify-content: center;
  padding: 28px 16px;
}
.ws-modal-book {
  width: 130px;
  aspect-ratio: 3 / 4;
  border-radius: 2px 6px 6px 2px;
  box-shadow: 6px 8px 24px rgba(0,0,0,0.4), 2px 2px 6px rgba(0,0,0,0.25);
  position: relative;
  transform: perspective(800px) rotateY(-12deg);
}
/* Page edges on modal book */
.ws-modal-book::after {
  content: ''; position: absolute;
  top: 2px; right: -8px; bottom: 2px; width: 8px;
  border-radius: 0 2px 2px 0;
  background: repeating-linear-gradient(to bottom, #f8f6f3 0px, #f8f6f3 2px, #dedad6 2px, #dedad6 3px);
  box-shadow: 2px 1px 5px rgba(0,0,0,0.2);
  z-index: -1;
}

/* Right: details */
.ws-modal-body {
  flex: 1;
  padding: 28px 24px 24px;
  display: flex; flex-direction: column;
}
.ws-modal-subject {
  font-size: 10px; font-weight: 700; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--book-color);
  margin-bottom: 6px; opacity: 0.7;
}
.ws-modal-title {
  font-size: 20px; font-weight: 800; color: #1a2332;
  line-height: 1.2; margin-bottom: 8px;
}
.ws-modal-desc { font-size: 14px; color: #555; line-height: 1.6; margin-bottom: 16px; }
.ws-modal-pills { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.ws-modal-pill {
  background: var(--book-light); color: var(--book-color);
  font-size: 12px; font-weight: 600;
  padding: 4px 12px; border-radius: 20px;
}
.ws-modal-note {
  font-size: 12px; color: #888; line-height: 1.5;
  margin-bottom: 20px; flex: 1;
  padding: 10px 12px;
  background: #f8f9fa; border-radius: 8px;
  border-left: 3px solid var(--book-color);
}
.ws-modal-download {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 13px;
  background: var(--book-color); color: white;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  letter-spacing: 0.01em;
}
.ws-modal-download:hover { opacity: 0.9; transform: translateY(-1px); }
.ws-modal-download-loading { opacity: 0.6; cursor: default; }
.ws-modal-download-loading:hover { opacity: 0.6; transform: none; }
.ws-modal-date {
  margin-left: auto;
  font-size: 11px; font-weight: 400; opacity: 0.75;
}
.ws-modal-unavailable {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px;
  background: #f8f9fa; border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 13px; color: #64748b;
}

.ws-modal-signin { display: flex; flex-direction: column; gap: 12px; }
.ws-modal-signin-text { font-size: 13px; color: #64748b; margin: 0; }
.ws-modal-signin-actions { display: flex; gap: 10px; }
.ws-modal-signin-actions .ws-modal-download { flex: 1; }
.ws-modal-download-outline {
  background: transparent !important;
  color: var(--book-color) !important;
  border: 2px solid var(--book-color) !important;
}
.ws-modal-download-outline:hover { opacity: 0.85; }
@keyframes ws-spin { to { transform: rotate(360deg); } }
.ws-spin { animation: ws-spin 1s linear infinite; }

/* Modal transition */
.ws-modal-enter-active, .ws-modal-leave-active { transition: all 0.25s ease; }
.ws-modal-enter-from, .ws-modal-leave-to { opacity: 0; }
.ws-modal-enter-from .ws-modal, .ws-modal-leave-to .ws-modal { transform: scale(0.94) translateY(12px); }
.ws-modal-enter-active .ws-modal, .ws-modal-leave-active .ws-modal { transition: all 0.25s ease; }

/* ── Responsive ── */
@media (max-width: 768px) {
  .ws-bc-row { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .ws-bc-bay { padding: 16px 14px 10px; }
  .ws-bc-wall { width: 20px; }
}
</style>
